<template>
  <div class="ap-sidebar--compact sr-compact-side-bar">
    <b-nav vertical>
      <div
        v-if="options.module"
        id="moduleTitle"
        :qa="`compact-sidebar-${options.module.name}`"
        class="ap-sidebar--compact__header ap-sidebar--compact__icon"
      >
        <fa-icon
          class="nav-icon"
          fixed-width
          :icon="options.module.icon"
        />
      </div>
      <b-popover
        target="moduleTitle"
        container="moduleTitle"
        triggers="hover"
        placement="rightbottom"
      >
        <div class="ap-sidebar--compact__pop">
          <div class="ap-sidebar--compact__pop-title d-flex align-items-center">
            <div>{{ $tc(options.module.name) }}</div>
          </div>
        </div>
      </b-popover>

      <!-- TODO: use slot -->
      <section-visibility-checks-compact
        v-if="viewAllAllowed"
        :is-incidents="isIncidents"
        :is-grossing="isGrossing"
        :is-diagnosis="isDiagnosis"
        :view-all-enabled="viewAllEnabled"
        @view-all-toggled="$emit('view-all-toggled', $event)"
      />
      <template v-if="isDiagnosis">
        <section-visibility-responsibilities
          v-if="sidebarConfigLoaded"
          is-compact
          :sidebar-store="store"
          @update-responsibilities="$emit('update-responsibilities', $event)"
        />

        <section-parent-checks-compact :sidebar-store="store" />
      </template>

      <div
        v-for="option in options.expanded"
        :key="option.id"
        class="w-100"
      >
        <template v-if="!option.hide">
          <div class="ap-sidebar--compact__block" />
          <div
            v-for="subOption in option.children"
            :key="subOption.id"
            class="ap-sidebar--compact__section"
          >
            <template v-if="!subOption.hide">
              <option-nav
                v-if="!subOption.hasChildren"
                :id="subOption.id"
                v-model="selected"
                no-lock
                class="ap-sidebar--compact__nav"
                :option="subOption"
                :default-option="defaultOption"
                :store="store"
                :icon="subOption.icon"
                :alticon="subOption.alticon"
                @setDefaultOption="setDefaultOption($event)"
              >
                <b-badge
                  v-if="counters && counters[subOption.id]"
                  class="ap-sidebar--compact__badge"
                  variant="vt-red"
                  pill
                >
                  {{ counters[subOption.id] }}
                </b-badge>
              </option-nav>
              <b-popover
                v-if="!subOption.hasChildren"
                :target="subOption.id"
                :container="subOption.id"
                triggers="hover"
                placement="rightbottom"
              >
                <div class="ap-sidebar--compact__pop ap-sidebar--compact__item">
                  <b-nav
                    class="ap-sidebar--compact__item-single"
                    vertical
                  >
                    <option-nav
                      v-model="selected"
                      :option="subOption"
                      :default-option="defaultOption"
                      :store="store"
                      @input="setParent()"
                      @setDefaultOption="setDefaultOption($event)"
                    />
                    <span class="link-label">
                      {{ $tc(customLabelSubOption(subOption, labelSubOption)) }}
                    </span>
                  </b-nav>
                </div>
              </b-popover>
              <div
                v-if="subOption.hasChildren"
                class="w-100"
              >
                <b-nav-item
                  :id="subOption.id"
                  class="ap-sidebar--compact__nav"
                >
                  <div class="">
                    <fa-icon
                      v-if="!subOption.alticon"
                      fixed-width
                      :icon="subOption.icon"
                    />
                    <i
                      v-if="subOption.alticon"
                      :class="subOption.alticon"
                      aria-hidden="true"
                    />
                  </div>
                </b-nav-item>
                <b-popover
                  :target="subOption.id"
                  :container="subOption.id"
                  triggers="hover"
                  placement="rightbottom"
                >
                  <div class="ap-sidebar--compact__pop ap-sidebar--compact__item">
                    <div class="ap-sidebar--compact__pop-title d-flex align-items-center">
                      <div>{{ $t(customLabelSubOption(subOption, labelSubOption)) }}</div>
                    </div>
                    <b-nav
                      class="ap-sidebar--compact__list"
                    >
                      <option-nav
                        v-for="sub in subOption.children"
                        :key="sub.id"
                        v-model="selected"
                        :no-lock="subOption.noLock"
                        :option="sub"
                        :default-option="defaultOption"
                        :store="store"
                        style="position:relative"
                        @input="setParent(subOption)"
                        @setDefaultOption="setDefaultOption($event)"
                      >
                        <template v-if="!sub.hide">
                          <div class="d-flex align-items-center justify-content-between flex-grow-1">
                            <span>{{ $tc(customLabelSubSubOption(sub, labelSubSubOption)) }}</span>
                            <b-badge
                              v-if="counters && counters[sub.id]"
                              class="count-badge"
                              variant="vt-red"
                              pill
                            >
                              {{ counters[sub.id] }}
                            </b-badge>
                          </div>
                        </template>
                      </option-nav>
                    </b-nav>
                  </div>
                </b-popover>
              </div>
            </template>
          </div>
        </template>
      </div>
    </b-nav>

    <b-nav
      class="flex-grow-1"
      vertical
    >
      <div class="ap-sidebar--compact__block" />

      <b-nav-item v-if="showTimeFilters">
        <sidebar-filters-time-filter-compact />
      </b-nav-item>

      <b-nav-item v-if="showCustomFilters">
        <sidebar-filters-custom-filter-compact
          :selected-option.sync="selectedOption"
          :default-option="defaultOption"
          :options="customOptions"
          :label="labelCustomOption"
          :custom-label="customLabelCustomOption"
          :store="store"
          :disable-add-filter="disableAddFilter"
          @create="$emit('click-add-filter')"
          @selected-parent-option="setParent()"
          @set-default-option-parent="setDefaultOption($event)"
          @update:selected-option="selected = $event"
        />
      </b-nav-item>
    </b-nav>

    <b-nav
      class="ap-sidebar--compact__footer p-1"
      vertical
    >
      <b-nav-item
        class="d-flex align-items-center justify-content-center"
        @click="$emit('expand')"
      >
        <b-button
          v-shortkey="['f1']"
          qa="compact-sidebar-expand-button"
          class="blank-button"
          @shortkey.prevent="$emit('expand')"
        >
          <i
            class="fas fa-chevron-right"
            aria-hidden="true"
          />
        </b-button>
      </b-nav-item>
    </b-nav>
  </div>
</template>

<script>
import OptionNav from '@/components/sideBar/OptionNav'
import SectionParentChecksCompact from '@/modules/diagnosisModule/sideBar/diagnosisSidebarChecks/SectionParentChecksCompact.vue'
import SectionVisibilityChecksCompact from '@/modules/diagnosisModule/sideBar/sidebarVisibilityChecks/SectionVisibilityChecksCompact.vue'
import SectionVisibilityResponsibilities from '@/modules/diagnosisModule/sideBar/diagnosisSidebarChecks/SectionVisibilityResponsibilities.vue'
import SidebarFiltersTimeFilterCompact from '@/components/sideBar/filters/time-filter/compact/sidebar-filters-time-filter-compact.vue'
import SidebarFiltersCustomFilterCompact from '@/components/sideBar/filters/custom-filter/compact/sidebar-filters-custom-filter-compact.vue'

export default {
    name: 'StudyRequestCompactSideBar',
    components: {
        OptionNav,
        SectionParentChecksCompact,
        SectionVisibilityChecksCompact,
        SectionVisibilityResponsibilities,
        SidebarFiltersTimeFilterCompact,
        SidebarFiltersCustomFilterCompact
    },
    props: {
        notRead: { type: Number },
        drafts: { type: Number },
        issues: { type: Number },
        options: { type: Object },
        customOptions: { type: Array, default: () => [] },
        selectedOption: { type: Object, default: null },
        defaultOption: { type: Object, default: null },
        disableAddFilter: { type: Boolean, default: false },
        store: { type: Object },
        isDiagnosis: { type: Boolean },
        isGrossing: { type: Boolean },
        isIncidents: { type: Boolean },
        viewAllAllowed: { type: Boolean, default: false },
        viewAllEnabled: { type: Boolean, default: false },
        showTimeFilters: { type: Boolean, default: false },
        showCustomFilters: { type: Boolean, default: true },
        counters: { type: Object },
        labelSubOption: { type: String, default: 'label' },
        customLabelSubOption: {
            type: Function,
            default(option, label) {
                if (option) {
                    return label ? option[label] : option
                }
                return ''
            }
        },
        labelSubSubOption: { type: String, default: 'label' },
        customLabelSubSubOption: {
            type: Function,
            default(option, label) {
                if (option) {
                    return label ? option[label] : option
                }
                return ''
            }
        },
        labelCustomOption: { type: String, default: 'label' },
        customLabelCustomOption: {
            type: Function,
            default(option, label) {
                if (option) {
                    return label ? option[label] : option
                }
                return ''
            }
        }
    },
    computed: {
        selected: {
            set(value) {
                this.$emit('update:selected-option', value)
            },
            get() {
                return this.selectedOption
            }
        },
        sidebarConfigLoaded() {
            return this.store?.loadedConfig
        }
    },
    methods: {
        setParent(parentOption) {
            this.$emit('selected-parent-option', parentOption)
        },
        setDefaultOption(ev) {
            this.$emit('setDefaultOptionParent', ev)
        }
    }
}
</script>

<style lang="scss">
    .sr-compact-side-bar .menu-popover-items.single {
        flex-direction: row !important;
        align-items: center;
        padding-top: 4px;
        padding-bottom:4px;
        overflow-y: hidden;
    }
    .sr-compact-side-bar .menu-popover-items.single .nav-link {
        background: transparent !important;
        padding-right: 0 !important;
    }
    .sr-compact-side-bar .menu-popover-items.single .link-label {
        color: vt-color(white-light);
    }
    .sr-compact-side-bar .arrow {
        display: none;
    }

    .sr-compact-side-bar .popover {
        border-radius: 0;
        border: 0;
        top: -14px !important;
        left: -1px !important;
        box-shadow: 0 2px 5px 0 #6a6a6a !important;
    }

    .sr-compact-side-bar .menu-popover-title {
        color: vt-color(sidebar-parent-font);
        height: 40px;
    }

    .sr-compact-side-bar .section-header .col{
        display: none;
    }

    .sr-compact-side-bar .section-parent-checks{
        width: 100%
    }

    .sr-compact-side-bar .check-parent .title{
        display: none !important;
    }

    .sr-compact-side-bar .check-parent{
        height: 42px;
    }

    .sr-compact-side-bar .option-nav-lock{
        padding-right: 0;
    }

    .sr-compact-side-bar .menu-popover .nav-link{
        padding: 0 10px !important;
    }

    .sr-compact-side-bar .menu-item.single-button .count-badge{
        right: 0;
        margin-right: 2px;
        bottom: 2px;
    }

    .sr-compact-side-bar .menu-popover .count-badge {
        position: relative !important;
    }

    .sr-compact-side-bar .option-label {
        display: none;
    }

    .sr-compact-side-bar .personal-link .option-label {
        display: block;
    }

    .sr-compact-side-bar .single-block .badge {
        font-size: 0.7rem !important;
    }

    .sr-compact-side-bar .single-button {
        height: 40px;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .sr-compact-side-bar .icon-box {
        margin-right: 0;
    }

    .sr-compact-side-bar .icon-box .icon-wrapper {
        display: flex !important;
        align-items: center;
        justify-content: center;
    }

    .sr-compact-side-bar .menu-popover a {
        color: vt-color(sidebar-child-font) !important;
    }

    .sr-compact-side-bar .menu-popover .nav-link:hover {
        background: vt-color(primary);
    }

    .sr-compact-side-bar .menu-popover .nav-link.active {
        justify-content: start !important;
    }

    .sr-compact-side-bar .menu-popover .icon-wrapper.locked {
        display: none !important;
    }

    .sr-compact-side-bar .menu-popover .option-nav:hover .icon-wrapper {
        display: none !important;
    }

    .sr-compact-side-bar .menu-popover .option-nav:hover .icon-wrapper.no-lock {
        display: flex !important;
    }

    .sr-compact-side-bar .add-personal-filter-link .nav-link {
        padding-right: .7rem !important;
        padding-left: .5rem !important;
        padding-top: .1rem !important;
        padding-bottom: .1rem !important;
    }

    .sr-compact-side-bar .single-block {
        position: relative;
    }

    .sr-compact-side-bar .single-block .badge {
        position: absolute;
        bottom: 3px;
        right: 5px;
    }
</style>

<style scoped lang="scss">
    .blank-button{
        padding: 0;
        margin: 0;
        background: transparent;
        border: transparent;
    }

    .filter-button {
        color: vt-color(sidebar-parent-icon) !important;
    }

    .pop-module-layout{
        margin-left: 1.3rem;
        min-height: 35px;
    }

    .pop-module-layout small{
        text-transform: uppercase;
    }

    .option-label-compact{
        height:10px;
        width: 100%;
        background: vt-color(sidebar-section-header-bg);
    }

    .pop-section .menu-popover-title{
        font-size: .8rem;
        text-transform: uppercase;
        font-weight: bold;
        text-align: center;
        color: vt-color(primary) !important;
        padding-left: 0 !important;
        padding-bottom: 0 !important;
    }

    $sideBarBorder: 1px solid #d8d8d8;

    .sr-compact-side-bar{
        padding-top: 0 !important;
        width: 3.7rem !important;
        -webkit-box-shadow: 1px 0 5px 0 vt-color(sidebar-box-shadow);
        -moz-box-shadow: 1px 0 5px 0 vt-color(sidebar-box-shadow);
        box-shadow: 1px 0 5px 0 vt-color(sidebar-box-shadow);
    }

    .nav-link{
        padding: 0 !important;
    }

    .menu-module{
        margin: 0 !important;
        width:100% !important;
    }

    .menu-popover-items.dropdown .nav-item.option-nav{
        width: 100%;
        overflow-y: auto;
    }

    .icon-wrapper {
        font-size: 1.2rem;
        margin: auto;
        width: 32px;
        height: 42px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: vt-color(primary);
    }

    .sr-compact-side-bar {
        display: flex;
        flex-direction: column;
        width: 63px;
        height: 100%;
        padding-top: 16px;
        .menu-item {
            position: relative;
            text-align: center;
            ::v-deep .nav-link {
                color: $base-text-color;
                padding: 0;
                &:hover {
                    color: $header-text-color;
                }
            }
        }
    }

    .top-menu {
        .menu-item {
            font-size: 19px;
        }
    }

    .top-menu ::v-deep .nav-link.active {
        color: $header-text-color;
        background: vt-color(primary);
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .middle-menu {
        flex-grow: 1;
        padding-bottom: 1rem;
        .menu-item {
            font-size: 19px;
        }
    }

    .bottom-menu {
        flex: 0 0 auto;
        background: vt-color(sidebar-bottom-menu-bg);
        height: 31px;
    }

    .icon-collapse{
        margin-top:5px;
    }

    .bottom-menu:hover{
        background: vt-color(sidebar-bottom-menu-hover);
    }

    .bottom-menu .blank-button {
        font-size: 1rem;
        background: transparent !important;
    }

    .menu-button {
        width: 100%;
        height: 42px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: vt-color(primary);
        font-size: 1.2rem;
    }

    .menu-button.parent-button{
        color: vt-color(sidebar-parent-font);
    }

    .menu-popover {
        width: 200px;
        font-size: .9rem;
        .menu-popover-title {
            margin-bottom: 0;
            padding-left: 1.3rem;
            padding-top: 0.5rem;
            padding-bottom: 0.5rem;
        }
        .menu-popover-items {
            font-size: 0.9rem;
            background: vt-color(sidebar-childs-bg);
            color: #303133;
            ::v-deep .nav-link {
                display: flex;
                align-items: center;
                padding-left: 0;
                padding-top: 0;
                padding-bottom: 0;
                &.active {
                    color: $header-text-color;
                }
            }
        }
    }

    @media (max-width: 1300px){
        .icon-wrapper {
            font-size: 1.1rem;
        }

        .menu-button {
            font-size: 1rem;
        }
    }

</style>
